import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FiSearch, FiFilter, FiTrendingUp, FiZap, FiShield, FiBarChart, FiDatabase, FiClock } from 'react-icons/fi';
import './LogManagement.css';

const LogManagement = () => {
  const features = [
    {
      icon: <FiZap />,
      title: "Centralized Log Collection",
      description: "Collect and centralize logs from various sources across your entire application stack."
    },
    {
      icon: <FiFilter />,
      title: "AI-Powered Analysis",
      description: "AI analyzes log data to identify patterns, anomalies, and root causes of issues."
    },
    {
      icon: <FiTrendingUp />,
      title: "Real-time Processing",
      description: "Process and analyze logs in real-time with eBPF-based data collection."
    },
    {
      icon: <FiShield />,
      title: "Compliance Monitoring",
      description: "Ensure compliance with automated log analysis and monitoring capabilities."
    },
    {
      icon: <FiBarChart />,
      title: "Efficient Troubleshooting",
      description: "Quickly identify and resolve issues with intelligent log correlation and analysis."
    },
    {
      icon: <FiDatabase />,
      title: "Cost-Effective Storage",
      description: "Reduce log management costs by up to 10x with efficient data processing."
    }
  ];

  const benefits = [
    "Centralized log collection from all sources",
    "AI-powered log analysis and pattern recognition",
    "Real-time log processing with eBPF technology",
    "Automated compliance monitoring and reporting",
    "Efficient troubleshooting with intelligent correlation",
    "Up to 10x cost reduction in log management"
  ];

  return (
    <div className="log-management-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Log Management</h1>
            <p className="hero-subtitle">
              Centralize and analyze logs from various sources for efficient troubleshooting and compliance
            </p>
            <div className="hero-actions">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/pricing" className="btn btn-primary">
                  Start Free Trial
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link to="/demo" className="btn btn-outline">
                  Request Demo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Powerful Log Management</h2>
            <p>Handle massive log volumes with enterprise-grade performance and reliability</p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div 
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>Why Choose Our Log Management?</h2>
              <p>
                Our log management platform is designed to handle the most demanding workloads 
                with lightning-fast search and real-time processing capabilities.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li 
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiSearch />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            <motion.div 
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <h4>Log Analytics Dashboard</h4>
                </div>
                <div className="dashboard-content">
                  <div className="metric">
                    <FiClock />
                    <span>Search Time: 0.2s</span>
                  </div>
                  <div className="metric">
                    <FiDatabase />
                    <span>Logs Processed: 2.5B</span>
                  </div>
                  <div className="metric">
                    <FiTrendingUp />
                    <span>Throughput: 1M/sec</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Pages Section */}
      <section className="related-pages" style={{ padding: '4rem 0', background: 'var(--background-alt)' }}>
        <div className="container">
          <h3 style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--text-primary)' }}>Explore Related Solutions</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', maxWidth: '1000px', margin: '0 auto' }}>
            <Link to="/observability-platform" style={{ padding: '1.5rem', background: 'var(--background)', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center', textDecoration: 'none', color: 'var(--text-primary)', transition: 'all 0.3s' }}>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--primary-600)' }}>Observability Platform</h4>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Complete observability solution</p>
            </Link>
            <Link to="/ai-monitoring" style={{ padding: '1.5rem', background: 'var(--background)', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center', textDecoration: 'none', color: 'var(--text-primary)', transition: 'all 0.3s' }}>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--primary-600)' }}>AI Monitoring</h4>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Intelligent monitoring tools</p>
            </Link>
            <Link to="/cloud-monitoring" style={{ padding: '1.5rem', background: 'var(--background)', borderRadius: '12px', border: '1px solid var(--border-color)', textAlign: 'center', textDecoration: 'none', color: 'var(--text-primary)', transition: 'all 0.3s' }}>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--primary-600)' }}>Cloud Monitoring</h4>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Multi-cloud monitoring</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Manage Your Logs?</h2>
            <p>Experience lightning-fast log search and real-time processing with our platform.</p>
            <div className="cta-actions">
              <Link to="/pricing" className="btn btn-primary">
                Start Free Trial
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Contact Sales
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LogManagement;
